/* DateTimePickerRenderer.js */

"use strict";
const _ = require("lodash");
const DatePicker = require("../DatePicker/DatePickerComponent");
const TimePicker = require("../TimePicker/TimePickerComponent");

class DateTimePickerRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("date-time-picker-gl"); //TODO Setup the root Element
        this._rootElem.setAttribute("tabindex", "-1");
        if (this._model.disabled) {
            this._rootElem.classList.add("disabled");
        }
        if (this._model.darkTheme) {
            this._rootElem.classList.add("dark");
        }
        if (this._model.theme) {
            this._rootElem.classList.add(this._model.theme);
        }
        if (!this._model.visible) {
            this._rootElem.classList.add("hidden");
        }
        this._rootElem.addEventListener("focus", (this._onFocused = this._onFocused.bind(this)));
        this._rootElem.addEventListener("blur", (this._onBlurred = this._onBlurred.bind(this)));
        this._rootElem.addEventListener("keydown", (this._onKeyDown = this._onKeyDown.bind(this)));
        this._titleBarContainerElem = document.createElement("title-bar-container-gl");
        this._pickerContainerElem = document.createElement("picker-container-gl");
        this._rootElem.appendChild(this._titleBarContainerElem);
        this._rootElem.appendChild(this._pickerContainerElem);
        this._rootElem.component = this._model;
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("ShowTime", (this._onShowTime = this._onShowTime.bind(this)));
        this._model.on("ShowDate", (this._onShowDate = this._onShowDate.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("ShowYears", (this._onShowYears = this._onShowYears.bind(this)));
        this._model.on("ShowDays", (this._onShowDays = this._onShowDays.bind(this)));
        this._model.on("ShowHours", (this._onShowHours = this._onShowHours.bind(this)));
        this._model.on("ShowMinutes", (this._onShowMinutes = this._onShowMinutes.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("View", (this._onView = this._onView.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
    }
    _unregisterListeners() {
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("ShowTime", this._onShowTime);
        this._model.removeListener("ShowDate", this._onShowDate);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("ShowYears", this._onShowYears);
        this._model.removeListener("ShowDays", this._onShowDays);
        this._model.removeListener("ShowHours", this._onShowHours);
        this._model.removeListener("ShowMinutes", this._onShowMinutes);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("View", this._onView);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
    }

    _onView() {
        this.render();
    }

    _onFocus() {
        this._rootElem.focus();
    }

    _onBlur() {
        this._rootElem.blur();
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    _onKeyDown(evt) {
        this._model._triggerUserKeyDown(evt);
    }

    _onFocused(evt) {
        this._model._triggerUserFocus(evt);
    }

    _onBlurred(evt) {
        this._model._triggerUserBlur(evt);
    }

    _onShowTime(o, n) {
        if (!_.isEqual(o, n)) {
            this._removeTitleBar();
            this._renderTitleBar();
        }
        if (this._timePicker) {
            this._model.view = this._model.showDate ? "days" : "hours";
        }
    }

    _onShowDate(o, n) {
        if (!_.isEqual(o, n)) {
            this._removeTitleBar();
            this._renderTitleBar();
        }
        if (this._timePicker) {
            this._model.view = this._model.showDate ? "days" : "hours";
        }
    }

    _onVisible(bool) {
        if (bool) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
        if (this._timePicker) {
            this._timePicker.setVisible(bool);
        }
        if (this._datePicker) {
            this._datePicker.setVisible(bool);
        }
    }

    _onDisabled(bool) {
        if (bool) {
            this._onDisable();
        } else {
            this._onEnable();
        }
    }

    _onDisable() {
        this._rootElem.classList.add("disabled");
        this._yearElem.removeEventListener("click", this._model.showYears);
        this._monthDayElem.removeEventListener("click", this._model.showDays);
        this._hourElem.removeEventListener("click", this._model.showHours);
        this._minutesElem.removeEventListener("click", this._model.showMinutes);
        this._meridiemElem.removeEventListener("click", this._onMeridiemClick);
        if (this._timePicker) {
            this._timePicker.disable();
        }
        if (this._datePicker) {
            this._datePicker.disable();
        }
    }

    _onEnable() {
        this._rootElem.classList.remove("disabled");
        this._yearElem.addEventListener(
            "click",
            (this._model.showYears = this._model.showYears.bind(this._model))
        );
        this._monthDayElem.addEventListener(
            "click",
            (this._model.showDays = this._model.showDays.bind(this._model))
        );
        this._hourElem.addEventListener(
            "click",
            (this._model.showHours = this._model.showHours.bind(this._model))
        );
        this._minutesElem.addEventListener(
            "click",
            (this._model.showMinutes = this._model.showMinutes.bind(this._model))
        );
        this._meridiemElem.addEventListener(
            "click",
            (this._onMeridiemClick = this._onMeridiemClick.bind(this))
        );
        if (this._timePicker) {
            this._timePicker.enable();
        }
        if (this._datePicker) {
            this._datePicker.enable();
        }
    }

    _onShowYears() {
        if (!this._datePicker) {
            this._renderDatePicker();
        } else {
            this._datePicker.view = "years";
        }
    }

    _onShowDays() {
        if (!this._datePicker) {
            this._renderDatePicker();
        } else {
            this._datePicker.view = "days";
        }
    }

    _onShowHours() {
        if (!this._timePicker) {
            this._renderTimePicker();
        } else {
            this._timePicker.view = "hours";
        }
    }

    _onShowMinutes() {
        if (!this._timePicker) {
            this._renderTimePicker();
        } else {
            this._timePicker.view = "minutes";
        }
    }

    _onValue() {
        if (this._timePicker) {
            this._timePicker.setValue({
                hour: this._model.value.get("hour"),
                minutes: this._model.value.get("minutes"),
            });
        }
        if (this._datePicker) {
            this._datePicker.setValue({
                year: this._model.value.get("year"),
                month: this._model.value.get("month"),
                date: this._model.value.get("date"),
            });
        }
        this._updateTitlebar();
    }

    _onDarkTheme(bool) {
        if (bool) {
            this._rootElem.classList.add("dark");
            if (this._timePicker) {
                this._timePicker.darkTheme = true;
            }
            if (this._datePicker) {
                this._datePicker.darkTheme = true;
            }
        } else {
            this._rootElem.classList.remove("dark");
            if (this._timePicker) {
                this._timePicker.darkTheme = false;
            }
            if (this._datePicker) {
                this._datePicker.darkTheme = false;
            }
        }
    }

    _onTheme(newVal, oldVal) {
        if (newVal) {
            this._rootElem.classList.add(newVal);
            this._titleBarContainerElem.classList.add(newVal);
            if (this._timePicker) {
                this._timePicker.theme = newVal;
            }
            if (this._datePicker) {
                this._datePicker.theme = newVal;
            }
        }
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
            this._titleBarContainerElem.classList.remove(oldVal);
        }
    }

    _updateTitlebar() {
        if (this._hourElem) {
            this._hourElem.innerHTML = this._model.value.format("hh");
        }
        if (this._minutesElem) {
            this._minutesElem.innerHTML = this._model.value.format(":mm");
        }
        if (this._meridiemElem) {
            this._meridiemElem.innerHTML = this._model.value.format("A");
        }
        if (this._monthDayElem) {
            this._monthDayElem.innerHTML = this._model.value.format("MMM DD");
        }
        if (this._yearElem) {
            this._yearElem.innerHTML = this._model.value.format("YYYY");
        }
    }

    _renderTitleBar() {
        if (!this._titleBarContainerElem.hasChildNodes()) {
            if (this._model.showDate) {
                // YEAR
                this._yearElem = document.createElement("year-gl");
                this._yearElem.addEventListener(
                    "click",
                    (this._model.showYears = this._model.showYears.bind(this._model))
                );
                this._titleBarContainerElem.appendChild(this._yearElem);

                // MONTH DAY
                this._monthDayElem = document.createElement("month-day-gl");
                this._monthDayElem.addEventListener(
                    "click",
                    (this._model.showDays = this._model.showDays.bind(this._model))
                );
                this._titleBarContainerElem.appendChild(this._monthDayElem);
            }

            // TIME
            if (this._model.showTime) {
                this._timeContainerElem = document.createElement("time-gl");
                // hours
                this._hourElem = document.createElement("hour-gl");
                this._hourElem.addEventListener(
                    "click",
                    (this._model.showHours = this._model.showHours.bind(this._model))
                );
                this._timeContainerElem.appendChild(this._hourElem);
                // minutes
                this._minutesElem = document.createElement("minutes-gl");
                this._minutesElem.addEventListener(
                    "click",
                    (this._model.showMinutes = this._model.showMinutes.bind(this._model))
                );
                this._timeContainerElem.appendChild(this._minutesElem);
                // meridiem
                this._meridiemElem = document.createElement("meridiem-gl");
                this._meridiemElem.addEventListener(
                    "click",
                    (this._onMeridiemClick = this._onMeridiemClick.bind(this))
                );
                this._timeContainerElem.appendChild(this._meridiemElem);
                this._titleBarContainerElem.appendChild(this._timeContainerElem);
            }
            this._updateTitlebar();
        }
    }

    _removeTitleBar() {
        if (this._titleBarContainerElem.hasChildNodes()) {
            if (this._timeContainerElem) {
                this._hourElem.remove();
                this._hourElem = undefined;
                this._minutesElem.remove();
                this._minutesElem = undefined;
                this._meridiemElem.remove();
                this._meridiemElem = undefined;
                this._timeContainerElem.remove();
                this._timeContainerElem = undefined;
            }
            this._monthDayElem.remove();
            this._monthDayElem = undefined;
            this._yearElem.remove();
            this._yearElem = undefined;
        }
    }

    _onMeridiemClick() {
        this._model._toggleMeridiem();
    }

    _renderDatePicker() {
        this._removeTimePicker();
        if (!this._datePicker) {
            let conf = {
                value: this._model.value,
                darkTheme: this._model.darkTheme,
                theme: this._model.theme,
                visible: this._model.visible,
                disabled: this._model.disabled,
                view: this._model.view,
            };
            this._datePicker = new DatePicker(conf);
            this._pickerContainerElem.appendChild(this._datePicker.renderer.rootElement);
            this._datePicker.on(
                "UserValue",
                (this._onDatePickerUserValue = this._onDatePickerUserValue.bind(this))
            );
        }
    }

    _removeDatePicker() {
        if (this._datePicker) {
            return this._datePicker.shutdown().then(() => {
                this._datePicker = undefined;
            });
        } else {
            return Promise.resolve();
        }
    }

    _onDatePickerUserValue(newVal /*, oldVal*/) {
        let date = {
            date: newVal.get("date"),
            month: newVal.get("month"),
            year: newVal.get("year"),
        };
        this._model.setValue(date, true);
    }

    _renderTimePicker() {
        this._removeDatePicker();
        if (!this._timePicker) {
            let conf = {
                value: this._model.value,
                darkTheme: this._model.darkTheme,
                theme: this._model.theme,
                visible: this._model.visible,
                disabled: this._model.disabled,
                view: this._model.view,
            };
            this._timePicker = new TimePicker(conf);
            this._timePicker.on("UserValue", (this._onTimeChange = this._onTimeChange.bind(this)));
            this._pickerContainerElem.appendChild(this._timePicker.renderer.rootElement);
        }
    }

    _removeTimePicker() {
        if (this._timePicker) {
            return this._timePicker.shutdown().then(() => {
                this._timePicker = undefined;
            });
        } else {
            return Promise.resolve();
        }
    }

    _onTimeChange(newVal) {
        let hm = {
            hour: newVal.get("hour"),
            minute: newVal.get("minute"),
        };
        this._model.setValue(hm, true);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        return Promise.all([this._removeDatePicker, this._removeTimePicker]).then(() => {
            this._titleBarContainerElem.remove();
            this._titleBarContainerElem = undefined;
            this._pickerContainerElem.remove();
            this._pickerContainerElem = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._model = undefined;
        });
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            this._renderTitleBar();
            switch (this._model.view) {
                case "years":
                case "days":
                    this._renderDatePicker();
                    break;
                case "hours":
                case "minutes":
                    this._renderTimePicker();
                    break;
            }
            resolve();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        this.render();
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    //TODO Private Render Methods here
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = DateTimePickerRenderer;
